import React, { useState } from 'react';
import { Form, Input, Button, Typography, message, Alert } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { customerAuthService } from '../services/customerAuth';

const { Title, Text } = Typography;

export const CustomerLogin: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Allow users to access login page even if already logged in
  // Redirect will happen after successful login in onLoginFinish

  const onLoginFinish = async (values: { ic_passport_no: string; password: string }) => {
    setLoading(true);
    setError(null);
    try {
      const { must_change_password } = await customerAuthService.login(values);
      message.success('Login successful!');
      if (must_change_password) {
        message.info('Please change your password before using the system.');
        navigate('/customer/change-password');
      } else {
      navigate('/customer/dashboard');
      }
    } catch (error: any) {
      console.error('Login error details:', error);
      let errorMessage = 'Login failed';
      
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
      } else if (error.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const onLoginFinishFailed = (errorInfo: any) => {
    console.log('Login form validation failed:', errorInfo);
    setError('Please fill in all required fields correctly');
  };

  return (
    <>
      <style>
        {`
          * {
            box-sizing: border-box;
          }
          
          body {
            margin: 0;
            padding: 0;
            overflow-x: hidden;
          }
        `}
      </style>
      <div style={{ 
        height: '100vh', 
        width: '100vw',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: '#f5f5f5',
      }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '40px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          maxWidth: '450px',
          width: '100%',
        }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <Title level={2} style={{ marginBottom: '8px', color: '#262626' }}>
              Customer Login
            </Title>
            <Text style={{ color: '#8c8c8c', fontSize: '16px' }}>
              Sign in to access your account
            </Text>
          </div>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
              style={{ marginBottom: '24px' }}
            />
          )}

          <Form
            name="customer-login"
            onFinish={onLoginFinish}
            onFinishFailed={onLoginFinishFailed}
            autoComplete="off"
            size="large"
            layout="vertical"
          >
            <Form.Item
              name="ic_passport_no"
              label="IC / Passport No"
              rules={[
                { required: true, message: 'Please enter your IC / Passport No' },
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Enter your IC / Passport No"
                style={{ height: '48px', fontSize: '16px' }}
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, message: 'Please enter your password' }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Enter your password"
                style={{ height: '48px', fontSize: '16px' }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: '16px' }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{ 
                  width: '100%', 
                  height: '48px',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                Login
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <Text style={{ color: '#8c8c8c', fontSize: '14px' }}>
              Need help? <a href="/customer/support" style={{ color: '#1890ff' }}>Contact Support</a>
            </Text>
          </div>
        </div>
      </div>
    </>
  );
};

